import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ChartDatePicker from './ChartDatePicker';
import { getBackendUrl } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

const RadioTopSongs = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayLimit, setDisplayLimit] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentWeek, setCurrentWeek] = useState(null);
  const [isHistorical, setIsHistorical] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'greek', 'international'
  const [allSongs, setAllSongs] = useState([]); // Store all songs
  const [greekSongs, setGreekSongs] = useState([]); // Greek songs only
  const [internationalSongs, setInternationalSongs] = useState([]); // International songs only

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchCurrentWeek();
    fetchRadioChart();
  }, []);

  const fetchCurrentWeek = async () => {
    try {
      const backendUrl = getBackendUrl();
      const response = await axios.get(`${backendUrl}/api/charts/current-week`);
      if (response.data.status === 'success') {
        setCurrentWeek(response.data.week_info);
      }
    } catch (error) {
      console.error('Error fetching current week:', error);
    }
  };

  const loadHistoricalChart = async (year, week) => {
    setLoading(true);
    setError(null);
    try {
      const backendUrl = getBackendUrl();
      const response = await axios.get(
        `${backendUrl}/api/charts/weekly/${year}/${week}`
      );
      
      if (response.data.status === 'success' && response.data.chart) {
        const chart = response.data.chart;
        setSongs(chart.songs || []);
        setCurrentWeek({
          year: chart.year,
          week: chart.week,
          week_range: chart.week_range
        });
        setIsHistorical(true);
      } else {
        throw new Error('No chart data found');
      }
    } catch (error) {
      console.error('Error loading historical chart:', error);
      setError(t('noChartDataForWeek'));
      // Fallback to current songs
      fetchRadioChart();
      setIsHistorical(false);
    } finally {
      setLoading(false);
    }
  };

  const handleWeekChange = (year, week) => {
    loadHistoricalChart(year, week);
  };

  const saveCurrentChart = async () => {
    if (!currentWeek || !songs.length) {
      alert(t('noDataToSave'));
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert(t('adminOnlyFeature'));
      return;
    }

    try {
      const backendUrl = getBackendUrl();
      const response = await axios.post(
        `${backendUrl}/api/charts/weekly/save`,
        {
          year: currentWeek.year,
          week: currentWeek.week,
          songs: songs
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.status === 'success') {
        alert(`✅ ${t('chartSavedSuccess')}\n${t('year')}: ${currentWeek.year}\n${t('weekFull')}: ${currentWeek.week}\n${t('songsCount')}: ${songs.length}`);
      }
    } catch (error) {
      console.error('Error saving chart:', error);
      if (error.response?.status === 403) {
        alert(`❌ ${t('adminOnlySaveChart')}`);
      } else {
        alert(`❌ ${t('saveFailed')}`);
      }
    }
  };

  const categorizeByLanguage = (songsArray) => {
    // Use backend categorization as the primary source (more accurate and comprehensive)
    const greek = [];
    const international = [];
    
    songsArray.forEach(song => {
      // Use backend category directly - it has better detection logic
      if (song.category === 'greek') {
        greek.push(song);
      } else if (song.category === 'international') {
        international.push(song);
      } else {
        // Fallback: If no category, use simple Greek character detection
        const artist = song.artists || song.artist || '';
        const title = song.song || song.title || '';
        const hasGreekChars = /[\u0370-\u03FF\u1F00-\u1FFF]/.test(artist + title);
        
        if (hasGreekChars) {
          greek.push(song);
        } else {
          international.push(song);
        }
      }
    });
    
    // Add category_rank (1 to N) for each category
    // This gives each song a sequential number within its category
    greek.forEach((song, index) => {
      song.category_rank = index + 1;
      song.original_rank = song.rank; // Keep original rank from Top 1000
    });
    
    international.forEach((song, index) => {
      song.category_rank = index + 1;
      song.original_rank = song.rank; // Keep original rank from Top 1000
    });
    
    return { greek, international };
  };

  const fetchRadioChart = async () => {
    try {
      const backendUrl = getBackendUrl();
      // Add cache-busting parameter to force fresh data
      const response = await axios.get(
        `${backendUrl}/api/admin/radio-chart/latest?_t=${Date.now()}`,
        {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );

      if (response.data.status === 'success') {
        const allSongsData = response.data.data.songs || [];
        setAllSongs(allSongsData);
        setSongs(allSongsData); // Default to all
        
        // Categorize songs
        const { greek, international } = categorizeByLanguage(allSongsData);
        
        // ALWAYS limit to TOP 300 from each category
        const greekTop300 = greek.slice(0, 300);
        const internationalTop300 = international.slice(0, 300);
        
        setGreekSongs(greekTop300);
        setInternationalSongs(internationalTop300);
        
        console.log(`📊 Categories: Greek ${greekTop300.length}, International ${internationalTop300.length}`);
      } else {
        setError(t('noChartData'));
      }
    } catch (err) {
      setError(t('analysisError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredSongs = songs
    .filter(song =>
      song.song.toLowerCase().includes(searchTerm.toLowerCase()) ||
      song.artists.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .slice(0, displayLimit);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  // SEO content based on language
  const seoContent = {
    el: {
      description: 'Radio Top Songs - Τα κορυφαία τραγούδια στα ελληνικά ραδιόφωνα. Εβδομαδιαίο chart με τα πιο δημοφιλή τραγούδια σε όλους τους σταθμούς της Ελλάδας.',
      keywords: 'radio top songs, ελληνικά charts, ραδιοφωνικά charts, top τραγούδια, greek radio chart, δημοφιλή τραγούδια, radio solutions'
    },
    en: {
      description: 'Radio Top Songs - Top songs on Greek radio stations. Weekly chart with the most popular songs across all stations in Greece.',
      keywords: 'radio top songs, greek charts, radio charts, top songs greece, greek radio chart, popular songs, radio solutions'
    }
  };
  const currentSeo = seoContent[language] || seoContent.el;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6FB] via-[#F5F0FA] to-[#F0EBF8] dark:from-[#1A0730] dark:via-[#2D0C4B] dark:to-[#1A0730] py-4 sm:py-8">
      {/* SEO Meta Tags - Title inherited from index.html (Radio Solutions Apps) */}
      <Helmet>
        <meta name="description" content={currentSeo.description} />
        <meta name="keywords" content={currentSeo.keywords} />
        <link rel="canonical" href="https://radio-apps.online/radio-top-songs" />
        <meta property="og:description" content={currentSeo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://radio-apps.online/radio-top-songs" />
      </Helmet>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4A1D7A] to-[#6B3FA0] hover:from-[#5E2D9E] hover:to-[#8A4FDB] text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('back')}
          </button>
        </div>
        
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D0C4B] dark:text-white mb-2">
            🎵 {t('radioTopSongsTitle').toUpperCase()}
          </h1>
          <p className="text-base sm:text-lg text-[#4A1D7A] dark:text-[#C4B5FD]">
            Greece | All Stations Daily Chart
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-2">
            <div className="inline-block bg-[#8A4FDB]/20 text-[#4A1D7A] dark:bg-[#6B3FA0] dark:text-white px-4 py-2 rounded-full text-sm font-semibold">
              {songs.length} {t('tracks')}
            </div>
            {currentWeek && (
              <div className="inline-block bg-[#6B3FA0]/20 text-[#4A1D7A] dark:bg-[#8A4FDB] dark:text-white px-4 py-2 rounded-full text-sm font-semibold">
                {t('weekFull')} {currentWeek.week} / {currentWeek.year}
              </div>
            )}
            {isHistorical && (
              <div className="inline-block bg-[#FF9F0A]/20 text-[#B45309] dark:bg-[#FF9F0A] dark:text-[#2D0C4B] px-4 py-2 rounded-full text-sm font-semibold">
                📅 {t('historicalData')}
              </div>
            )}
          </div>
        </div>

        {/* Date Picker */}
        <div className="mb-6">
          <ChartDatePicker 
            currentWeek={currentWeek} 
            onWeekChange={handleWeekChange}
          />
        </div>

        {/* Category Tabs */}
        <div className="mb-6">
          <div className="bg-white rounded-lg shadow-md p-2">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setActiveTab('all');
                  setSongs(allSongs);
                }}
                className={`flex-1 sm:flex-none px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  activeTab === 'all'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🎵 {t('allSongs')} ({allSongs.length})
              </button>
              <button
                onClick={() => {
                  setActiveTab('greek');
                  setSongs(greekSongs);
                }}
                className={`flex-1 sm:flex-none px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  activeTab === 'greek'
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🇬🇷 {t('greekSongs')} ({greekSongs.length})
              </button>
              <button
                onClick={() => {
                  setActiveTab('international');
                  setSongs(internationalSongs);
                }}
                className={`flex-1 sm:flex-none px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  activeTab === 'international'
                    ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🌍 {t('internationalSongs')} ({internationalSongs.length})
              </button>
            </div>
          </div>
        </div>

        {/* Admin: Save Current Chart Button */}
        {!isHistorical && currentWeek && (
          <div className="mb-6 text-center">
            <button
              onClick={saveCurrentChart}
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-md transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              {t('saveCurrentChart')} ({t('weekLabel')} {currentWeek.week})
            </button>
          </div>
        )}

        {/* Search & Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder={`🔍 ${t('searchSongArtist')}`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>

            {/* Display Limit */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{t('display')}:</label>
              <select
                value={displayLimit}
                onChange={(e) => setDisplayLimit(Number(e.target.value))}
                className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value={50}>Top 50</option>
                <option value={100}>Top 100</option>
                <option value={200}>Top 200</option>
                <option value={500}>Top 500</option>
              </select>
            </div>
          </div>
        </div>

        {/* Songs Table - Desktop View */}
        <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-600 to-purple-600">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">
                    {activeTab === 'all' ? 'Rank' : '#'}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">
                    Change
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Song
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Artist(s)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Label
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">
                    Plays
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">
                    Plays evolution
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">
                    WOC
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSongs.map((song, index) => (
                  <tr
                    key={index}
                    className={`hover:bg-blue-50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                  >
                    {/* Rank - Show category_rank for Greek/International tabs, original rank for All */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex flex-col items-center">
                        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-bold text-white ${
                          (activeTab !== 'all' ? song.category_rank : song.rank) <= 3 
                            ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                          (activeTab !== 'all' ? song.category_rank : song.rank) <= 10 
                            ? 'bg-gradient-to-br from-blue-500 to-blue-700' :
                          'bg-gray-500'
                        }`}>
                          {activeTab !== 'all' ? song.category_rank : song.rank}
                        </span>
                        {/* Show original rank from Top 1000 when in category tabs */}
                        {activeTab !== 'all' && (
                          <span className="text-xs text-gray-400 mt-1" title="Θέση στο Top 1000">
                            (#{song.rank})
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Change */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {song.change !== 0 && (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                          song.change > 0
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {song.change > 0 ? `↑ +${song.change}` : `↓ ${song.change}`}
                        </span>
                      )}
                      {song.change === 0 && (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>

                    {/* Song */}
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900">{song.song}</p>
                    </td>

                    {/* Artist */}
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700">{song.artists}</p>
                    </td>

                    {/* Label */}
                    <td className="px-4 py-3">
                      <p className="text-xs text-gray-500">{song.label || '—'}</p>
                    </td>

                    {/* Plays */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-bold">
                        {song.plays || 0}
                      </span>
                    </td>

                    {/* Plays Evolution */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {song.plays_evolution !== 0 && (
                        <span className={`text-sm font-semibold ${
                          song.plays_evolution > 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          {song.plays_evolution > 0 ? `+${song.plays_evolution}` : song.plays_evolution}
                        </span>
                      )}
                      {song.plays_evolution === 0 && (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>

                    {/* WOC (Weeks On Chart) */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-semibold">
                        {song.weeks_on_chart || song.days_on_chart}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Songs List - Mobile View */}
        <div className="md:hidden space-y-4">
          {filteredSongs.map((song, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500"
            >
              {/* Rank Badge */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex flex-col items-center">
                  <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full font-bold text-white text-lg ${
                    (activeTab !== 'all' ? song.category_rank : song.rank) <= 3 
                      ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                    (activeTab !== 'all' ? song.category_rank : song.rank) <= 10 
                      ? 'bg-gradient-to-br from-blue-500 to-blue-700' :
                    'bg-gray-500'
                  }`}>
                    #{activeTab !== 'all' ? song.category_rank : song.rank}
                  </span>
                  {/* Show original rank when in category tabs */}
                  {activeTab !== 'all' && (
                    <span className="text-xs text-gray-400 mt-1">
                      Top 1000: #{song.rank}
                    </span>
                  )}
                </div>

                {/* Change Badge */}
                {song.change !== 0 && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                    song.change > 0
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {song.change > 0 ? `↑ +${song.change}` : `↓ ${song.change}`}
                  </span>
                )}
              </div>

              {/* Song Info */}
              <div className="mb-3">
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  {song.song}
                </h3>
                <p className="text-sm text-gray-600 mb-1">
                  🎤 {song.artists}
                </p>
                {song.label && (
                  <p className="text-xs text-gray-500">
                    🏢 {song.label}
                  </p>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-200">
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Plays</p>
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-bold">
                    {song.plays || 0}
                  </span>
                </div>

                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Plays Δ</p>
                  <p className={`text-sm font-semibold ${
                    song.plays_evolution > 0 ? 'text-green-600' :
                    song.plays_evolution < 0 ? 'text-red-600' :
                    'text-gray-400'
                  }`}>
                    {song.plays_evolution !== 0
                      ? (song.plays_evolution > 0 ? `+${song.plays_evolution}` : song.plays_evolution)
                      : '—'
                    }
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">WOC</p>
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-800 text-sm font-semibold">
                    {song.weeks_on_chart || song.days_on_chart}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {filteredSongs.length < songs.length && (
          <div className="text-center mt-8">
            <button
              onClick={() => setDisplayLimit(prev => Math.min(prev + 50, 500))}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              {t('showMore')}
            </button>
          </div>
        )}

        {/* No Results */}
        {filteredSongs.length === 0 && searchTerm && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {t('noResultsFor')} &quot;{searchTerm}&quot;
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RadioTopSongs;
